import { MathUtils, MOUSE, PerspectiveCamera, Vector3 } from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { percentForDistance } from './scene-geometry.util';

// The 3D preview's viewer operations - Rotate/Pan tool, Alt-drag pan override, zoom slider and -/+
// steps, and an animated Reset view - for pages that show the shared preview rig (createPreviewRig)
// without the beta designer's own panel-focus logic. Mirrors the beta designer's own
// setViewerTool()/resetView()/onZoomSliderInput()/stepCameraZoom() (design-3d-redesign.component.ts),
// including the same change-detection thresholds for "has the view moved off its default".

// Step size (out of the 0-100 zoom range) the zoom -/+ buttons move per click.
export const CAMERA_ZOOM_SLIDER_STEP = 10;
// Squared-distance thresholds / ratio for "has a pan / zoom / rotate moved the camera off its
// default" - large enough to ignore OrbitControls' own per-frame floating-point noise.
const PAN_OFFSET_EPSILON_SQ = 1e-6;
const ZOOM_OFFSET_EPSILON_RATIO = 1e-3;
const ROTATE_OFFSET_EPSILON_SQ = 1e-4;
const RESET_DURATION_MS = 600;

export type ViewerTool = 'rotate' | 'pan';

export class PreviewViewControls {
  tool: ViewerTool = 'rotate';
  // True once a pan, zoom or rotate has moved the camera off the default view - drives whether
  // the Reset view button shows.
  viewChanged = false;
  zoomPercent = 0;

  private readonly orbitTarget = new Vector3();
  private readonly defaultDirection = new Vector3();
  private defaultDistance = 0;
  private animation?: {
    fromPosition: Vector3;
    toPosition: Vector3;
    fromTarget: Vector3;
    toTarget: Vector3;
    start: number;
    duration: number;
  };

  constructor(
    private readonly camera: PerspectiveCamera,
    private readonly controls: OrbitControls,
  ) {
    controls.addEventListener('change', this.onControlsChange);
    // Alt-drag pans instead of rotating - swaps which gesture the left mouse button performs
    // rather than adding a second permanent control, since OrbitControls picks the drag mode once
    // at pointerdown time.
    window.addEventListener('keydown', this.onModifierKey);
    window.addEventListener('keyup', this.onModifierKey);
    window.addEventListener('blur', this.onBlur);
  }

  // Records the view "Reset view" returns to and every change is measured against - call after
  // fitting the camera to the loaded box.
  setBaseline(center: Vector3, direction: Vector3, distance: number): void {
    this.orbitTarget.copy(center);
    this.defaultDirection.copy(direction).normalize();
    this.defaultDistance = distance;
    this.viewChanged = false;
    this.zoomPercent = this.currentZoomPercent();
  }

  setTool(tool: ViewerTool): void {
    this.tool = tool;
    this.applyTool(false);
  }

  // Undoes a pan, zoom and/or rotate: recenters the pivot and dollies back to the default distance
  // along the default direction, as a smooth tween rather than a snap.
  resetView(): void {
    this.animation = {
      fromPosition: this.camera.position.clone(),
      toPosition: this.orbitTarget.clone().addScaledVector(this.defaultDirection, this.defaultDistance),
      fromTarget: this.controls.target.clone(),
      toTarget: this.orbitTarget.clone(),
      start: performance.now(),
      duration: RESET_DURATION_MS,
    };
  }

  // Dollies straight to the distance a 0-100 slider percent represents (0 = zoomed all the way out),
  // keeping the camera's current orientation.
  setZoomPercent(percent: number): void {
    const { minDistance, maxDistance } = this.controls;
    if (!Number.isFinite(minDistance) || !Number.isFinite(maxDistance) || maxDistance <= minDistance) {
      return;
    }
    this.animation = undefined;
    this.zoomPercent = percent;
    const distance = maxDistance - (percent / 100) * (maxDistance - minDistance);
    const direction = this.camera.position.clone().sub(this.controls.target).normalize();
    this.camera.position.copy(this.controls.target).addScaledVector(direction, distance);
  }

  stepZoom(direction: 1 | -1): void {
    this.setZoomPercent(MathUtils.clamp(this.zoomPercent + direction * CAMERA_ZOOM_SLIDER_STEP, 0, 100));
  }

  // Call once per frame, before controls.update().
  step(): void {
    if (!this.animation) {
      return;
    }
    const { fromPosition, toPosition, fromTarget, toTarget, start, duration } = this.animation;
    const t = Math.min((performance.now() - start) / duration, 1);
    const eased = 1 - Math.pow(1 - t, 3);
    this.camera.position.lerpVectors(fromPosition, toPosition, eased);
    this.controls.target.lerpVectors(fromTarget, toTarget, eased);
    if (t >= 1) {
      this.animation = undefined;
    }
  }

  dispose(): void {
    this.controls.removeEventListener('change', this.onControlsChange);
    window.removeEventListener('keydown', this.onModifierKey);
    window.removeEventListener('keyup', this.onModifierKey);
    window.removeEventListener('blur', this.onBlur);
  }

  private applyTool(altOverride: boolean): void {
    const pan = altOverride || this.tool === 'pan';
    this.controls.mouseButtons.LEFT = pan ? MOUSE.PAN : MOUSE.ROTATE;
    this.controls.enablePan = pan;
  }

  private currentZoomPercent(): number {
    return percentForDistance(
      this.camera.position.distanceTo(this.controls.target),
      this.controls.minDistance,
      this.controls.maxDistance,
      this.zoomPercent,
    );
  }

  private readonly onModifierKey = (e: KeyboardEvent): void => this.applyTool(e.altKey);
  private readonly onBlur = (): void => this.applyTool(false);

  private readonly onControlsChange = (): void => {
    const distance = this.camera.position.distanceTo(this.controls.target);
    const panned = this.controls.target.distanceToSquared(this.orbitTarget) > PAN_OFFSET_EPSILON_SQ;
    const zoomed = Math.abs(distance - this.defaultDistance) > this.defaultDistance * ZOOM_OFFSET_EPSILON_RATIO;
    const direction = this.camera.position.clone().sub(this.controls.target).normalize();
    const rotated = direction.distanceToSquared(this.defaultDirection) > ROTATE_OFFSET_EPSILON_SQ;
    this.viewChanged = panned || zoomed || rotated;
    this.zoomPercent = percentForDistance(distance, this.controls.minDistance, this.controls.maxDistance, this.zoomPercent);
  };
}
